
import Vue from 'vue'
import { apiService } from '@/service/api.service'

/* eslint-disable camelcase */
interface LicenceType { id: number; label: string; deviceType: string | null }
interface Recipient { email: string; name: string; orderNumber: string }
interface BulkResult { email: string; name: string; orderNumber: string; color: string; icon: string; message: string; data?: Record<string, unknown> }
/* eslint-enable camelcase */

export default Vue.extend({
  name: 'SendActivationKeyDialog',
  props: { value: { type: Boolean, default: false } },
  data () {
    return {
      formValid: false,
      sending: false,
      sentCount: 0,
      loadingLicences: false,
      licenceTypes: [] as LicenceType[],
      bulkResults: [] as BulkResult[],
      recipients: [{ email: '', name: '', orderNumber: '' }] as Recipient[],
      formData: { licenceTypeId: null as number | null, duration_days: 365, quantity: 1, restrict_to_user: true, notes: '' },
      emailRules: [
        (v: string) => !!v || 'Email is required',
        (v: string) => /.+@.+\..+/.test(v.trim()) || 'Enter a valid email'
      ]
    }
  },
  computed: {
    dialog: {
      get (): boolean { return this.value },
      set (val: boolean) { this.$emit('input', val) }
    },
    licenceTypeItems (): { text: string; value: number; deviceType: string | null }[] {
      return this.licenceTypes.map((l) => ({ text: l.label, value: l.id, deviceType: l.deviceType }))
    },
    canSend (): boolean {
      return !this.sending && this.formValid && this.recipients.length > 0 &&
        !!this.formData.licenceTypeId && this.formData.duration_days > 0 &&
        this.recipients.every((r: Recipient) => /.+@.+\..+/.test(r.email.trim()))
    }
  },
  mounted () { this.loadLicenceTypes() },
  methods: {
    async loadLicenceTypes () {
      this.loadingLicences = true
      try {
        const response = await apiService.get('/get_all_licences_info')
        /* eslint-disable camelcase */
        this.licenceTypes = (response.data.licences || []).map((l: Record<string, unknown>) => ({
          id: l.id, label: l.label, deviceType: l.device_type_restriction || null
        }))
        /* eslint-enable camelcase */
      } catch (e) { console.error('Error loading licence types:', e) } finally { this.loadingLicences = false }
    },
    isValidEmail (email: string): boolean { return /.+@.+\..+/.test((email || '').trim()) },
    addRecipient () { this.recipients.push({ email: '', name: '', orderNumber: '' }) },
    removeRecipient (index: number) { this.recipients.splice(index, 1) },
    onDialogChange (val: boolean) { if (!val) this.closeDialog() },
    closeDialog () { this.resetForm(); this.dialog = false; this.$emit('close') },
    resetForm () {
      this.recipients = [{ email: '', name: '', orderNumber: '' }]
      this.formData = { licenceTypeId: null, duration_days: 365, quantity: 1, restrict_to_user: true, notes: '' }
      this.bulkResults = []
      this.sentCount = 0
      const form = this.$refs.sendForm as Vue & { resetValidation: () => void }
      if (form) form.resetValidation()
    },
    async sendKeys () {
      const form = this.$refs.sendForm as Vue & { validate: () => boolean }
      if (!form.validate()) return
      this.sending = true; this.bulkResults = []; this.sentCount = 0
      for (const recipient of this.recipients) {
        const email = recipient.email.trim().toLowerCase()
        try {
          /* eslint-disable camelcase */
          const payload = {
            email,
            recipient_name: recipient.name.trim() || undefined,
            order_number: recipient.orderNumber.trim() || undefined,
            licence_type_id: this.formData.licenceTypeId,
            duration_days: this.formData.duration_days,
            quantity: this.formData.quantity,
            restrict_to_user: this.formData.restrict_to_user,
            notes: this.formData.notes || undefined
          }
          /* eslint-enable camelcase */
          const response = await apiService.post('/licences/activation-keys/send', payload)
          if (response.data.ok) {
            const data = response.data.data
            this.$emit('sent', data)
            if (!data.user_registered) {
              this.bulkResults.push({
                email,
                name: recipient.name,
                orderNumber: recipient.orderNumber,
                color: data.email_sent ? 'warning' : 'error',
                icon: data.email_sent ? 'mdi-key-plus' : 'mdi-alert-circle',
                message: data.email_sent
                  ? `Not registered – key "${data.activation_code}" created & invitation sent`
                  : `Not registered – key "${data.activation_code}" created (invitation email failed)`,
                data: data as Record<string, unknown>
              })
            } else {
              this.bulkResults.push({
                email,
                name: recipient.name,
                orderNumber: recipient.orderNumber,
                color: data.email_sent ? 'success' : 'warning',
                icon: data.email_sent ? 'mdi-check-circle' : 'mdi-alert',
                message: data.email_sent
                  ? `Key "${data.activation_code}" created & sent`
                  : `Key "${data.activation_code}" created (email failed)`,
                data: data as Record<string, unknown>
              })
            }
          } else {
            this.bulkResults.push({ email, name: recipient.name, orderNumber: recipient.orderNumber, color: 'error', icon: 'mdi-close-circle', message: response.data.message || 'Error sending key' })
          }
        } catch (error) {
          const err = error as { response?: { data?: { message?: string } } }
          this.bulkResults.push({ email, name: recipient.name, orderNumber: recipient.orderNumber, color: 'error', icon: 'mdi-close-circle', message: err.response?.data?.message || 'Request failed' })
        }
        this.sentCount++
      }
      this.sending = false
      this.$emit('bulk-complete', this.bulkResults)
    }
  }
})
